import {
  FmvChartsData,
  FmvExtremum,
  PieChartCategory,
  FmvChartsFinData,
} from '../../pojo/fmv-charts-classes';
import { PpChartsMenuValues } from '../../pojo/fmv-charts-enums';
import {
  FairValueByCompanyBarChartsData,
  FmvBarData,
  FmvPieData,
} from '../calculations-service/fair-value-and-pp-charts.service';
import { PortfoliosPerformanceBarChartData } from '../../../../../chart/ChartAuxiliary';

export class FmvFilterService {

  /**
   * Calculate initial min and max values for all charts in both fmv and investment.
   *
   * @param chartData financial data for all charts
   * @returns FmvExtremum object with calculated min and max values for each category
   */
  static getInitialMinMaxValues(chartData: FmvChartsData): FmvExtremum {
    const extremum = new FmvExtremum();

    const calculateExtremums = (data: FmvChartsFinData, type: keyof FmvExtremum): void => {
      const categories = Object.keys(new PieChartCategory()) as (keyof PieChartCategory)[];

      categories.forEach((category) => {
        const values = data.pieChart[category]?.map((item) => item.y) || [];
        extremum[type].max[category] = FmvFilterService.calculateMaxValue(values);
        extremum[type].min[category] = FmvFilterService.calculateMinValue(values);
      });
    };

    calculateExtremums(chartData.fmv, PpChartsMenuValues.fmv);
    calculateExtremums(chartData.investment, PpChartsMenuValues.investment);

    return extremum;
  }

  /**
   * Filter fin. chart data based on selected tab, menu value and filter values
   *
   * @param chartData fin data from chart
   * @param selectedTabKey selected tab (byCompany, byIndustry...)
   * @param minValue min value in filter
   * @param maxValue max value in filter
   * @param selectedMenu selected menu value (fmv or investment)
   */
  static filterDataByKey(
    chartData: FmvChartsFinData,
    selectedTabKey: keyof PieChartCategory,
    minValue: number,
    maxValue: number,
    selectedMenu: PpChartsMenuValues,
  ): FmvChartsFinData {
    const filteredPieChart = this.filterPieChart(chartData.pieChart, minValue, maxValue);
    const filteredBarChart = this.filterBarChart(chartData.barChart, minValue, maxValue, selectedMenu);

    return {
      ...chartData,
      pieChart: filteredPieChart,
      barChart: filteredBarChart,
    };
  }

  private static filterPieChart(
    pieChart: FmvPieData,
    minValue: number,
    maxValue: number,
  ): FmvPieData {
    return Object.keys(pieChart).reduce((acc, key) => {
      acc[key] = pieChart[key].filter((item) => item.y >= minValue && item.y <= maxValue);
      return acc;
    }, {} as FmvPieData);
  }

  private static filterBarChart(
    barChart: FmvBarData,
    minValue: number,
    maxValue: number,
    selectedMenu: PpChartsMenuValues,
  ): FmvBarData {
    return Object.keys(barChart).reduce((acc, key) => {
      if (key === 'byCompany') {
        acc[key] = this.filterByCompany(barChart[key], minValue, maxValue, selectedMenu);
      } else {
        acc[key] = this.filterByMainData(barChart[key], minValue, maxValue, selectedMenu);
      }
      return acc;
    }, {} as FmvBarData);
  }

  private static filterByCompany(
    byCompany: FairValueByCompanyBarChartsData[],
    minValue: number,
    maxValue: number,
    selectedMenu: PpChartsMenuValues,
  ): FairValueByCompanyBarChartsData[] {
    return byCompany.filter((item) => {
      const fmv = item.gain + item.cost;
      return selectedMenu === PpChartsMenuValues.fmv
        ? fmv >= minValue && fmv <= maxValue
        : item.cost >= minValue && item.cost <= maxValue;
    });
  }

  private static filterByMainData(
    chartData: PortfoliosPerformanceBarChartData,
    minValue: number,
    maxValue: number,
    selectedMenu: PpChartsMenuValues,
  ): PortfoliosPerformanceBarChartData {
    return {
      ...chartData,
      mainData: chartData.mainData.filter((item) =>
        (selectedMenu === PpChartsMenuValues.fmv
          ? item.sum >= minValue && item.sum <= maxValue
          : item.investment.y >= minValue && item.investment.y <= maxValue),
      ),
      drilldownData: chartData.drilldownData,
    };
  }


  private static calculateMaxValue(dataArray: number[]): number {
    if (!dataArray.length) {
      return 0;
    }
    return Math.ceil(Math.max(...dataArray));
  }

  private static calculateMinValue(dataArray: number[]): number {
    if (!dataArray.length) {
      return 0;
    }
    return Math.ceil(Math.min(...dataArray));
  }
}
